import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiPost, apiPatch, apiDelete } from '@/lib/api';
import { showToast } from '@/components/Toast';
import { FileNode } from '@/lib/fileUtils';
import {
  useConfirmDialog,
  UseConfirmDialogReturn,
} from '../ui/useConfirmDialog';

export interface UseFolderOperationsOptions {
  projectId: string;
  fileTree: FileNode[];
  selectedFileIds: Set<string>;
  getCurrentFolderId: () => string | null;
  resetProjectFilesCache: (projectId: string) => void;
  loadProjectFilesFromStore: (projectId: string) => Promise<void>;
  clearFileSelection: () => void;
  // 预览联动回调（可选）
  previewFilePath?: string | null; // 当前预览的文件路径
  onClosePreview?: () => void; // 关闭预览
  onUpdatePreview?: (
    newFilename: string,
    newFilePath: string,
    newFileUrl: string
  ) => void; // 更新预览
}

export interface UseFolderOperationsReturn {
  // Create folder state
  isCreateFolderModalOpen: boolean;
  newFolderName: string;
  isCreatingFolder: boolean;
  newFolderNameValidation: { valid: boolean; error: string };
  setIsCreateFolderModalOpen: (open: boolean) => void;
  setNewFolderName: (name: string) => void;
  handleCreateFolder: () => Promise<void>;

  // Edit folder state
  isEditFolderModalOpen: boolean;
  editingFolder: FileNode | null;
  editFolderName: string;
  isEditingFolder: boolean;
  editFolderNameValidation: { valid: boolean; error: string };
  setIsEditFolderModalOpen: (open: boolean) => void;
  setEditFolderName: (name: string) => void;
  setEditingFolder: (folder: FileNode | null) => void;
  handleOpenEditFolder: (folder: FileNode, event: React.MouseEvent) => void;
  handleEditFolder: () => Promise<void>;

  // Delete folder
  handleDeleteFolder: (folder: FileNode, event: React.MouseEvent) => void;

  // Delete file
  handleDeleteFile: (file: FileNode, event: React.MouseEvent) => void;

  // Download file
  handleDownloadFile: (
    file: FileNode,
    event?: React.MouseEvent
  ) => Promise<void>;

  // Batch operations
  handleBatchDelete: () => Promise<void>;
  handleBatchDownload: () => Promise<void>;

  // Confirm dialog props (for file/folder delete operations)
  fileConfirmDialogProps: UseConfirmDialogReturn['dialogProps'];

  // Move operations
  isMoveModalOpen: boolean;
  moveModalPath: FileNode[];
  moveTargetPath: string | null;
  isMoving: boolean;
  moveModalCurrentNodes: FileNode[];
  setIsMoveModalOpen: (open: boolean) => void;
  setMoveModalPath: React.Dispatch<React.SetStateAction<FileNode[]>>;
  setMoveTargetPath: (path: string | null) => void;
  handleOpenMoveModal: () => void;
  handleMoveSelectFolder: (folder: FileNode) => void;
  handleMoveConfirm: () => Promise<void>;
}

// Folder name validation
const validateFolderName = (
  name: string
): { valid: boolean; error: string } => {
  if (!name.trim()) {
    return { valid: false, error: '' };
  }
  if (name.length > 32) {
    return {
      valid: false,
      error: 'Folder name must be 30 characters or less',
    };
  }
  // Filter unsafe characters: < > : " / \ | ? *
  const unsafeChars = /[<>:"/\\|?*]/;
  if (unsafeChars.test(name)) {
    return {
      valid: false,
      error: 'Folder name contains invalid characters',
    };
  }
  // Check for control characters (ASCII 0-31)
  const hasControlChars = name.split('').some(char => char.charCodeAt(0) < 32);
  if (hasControlChars) {
    return {
      valid: false,
      error: 'Folder name contains invalid characters',
    };
  }
  return { valid: true, error: '' };
};

// Helper: build map from id -> path
const buildIdPathMap = (nodes: FileNode[]) => {
  const map = new Map<string, string>();
  const traverse = (list: FileNode[]) => {
    for (const n of list) {
      if (n.id) map.set(n.id, n.path || n.name);
      if (n.children && n.children.length > 0) traverse(n.children);
    }
  };
  traverse(nodes);
  return map;
};

export function useFolderOperations({
  projectId,
  fileTree,
  selectedFileIds,
  getCurrentFolderId,
  resetProjectFilesCache,
  loadProjectFilesFromStore,
  clearFileSelection,
  previewFilePath,
  onClosePreview,
  onUpdatePreview,
}: UseFolderOperationsOptions): UseFolderOperationsReturn {
  const { t } = useTranslation();

  // 使用 ref 存储预览相关回调，避免频繁触发 useCallback 重建
  const previewFilePathRef = useRef(previewFilePath);
  const onClosePreviewRef = useRef(onClosePreview);
  const onUpdatePreviewRef = useRef(onUpdatePreview);
  previewFilePathRef.current = previewFilePath;
  onClosePreviewRef.current = onClosePreview;
  onUpdatePreviewRef.current = onUpdatePreview;

  // Create folder state
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // Edit folder state
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<FileNode | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [isEditingFolder, setIsEditingFolder] = useState(false);

  // Unified confirm dialog for file/folder delete operations
  const { confirmWithAction, dialogProps: fileConfirmDialogProps } =
    useConfirmDialog();

  // Move state
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [moveModalPath, setMoveModalPath] = useState<FileNode[]>([]);
  const [moveTargetPath, setMoveTargetPath] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  // Validation states
  const newFolderNameValidation = useMemo(
    () => validateFolderName(newFolderName),
    [newFolderName]
  );
  const editFolderNameValidation = useMemo(
    () => validateFolderName(editFolderName),
    [editFolderName]
  );

  // Get selected file paths
  const getSelectedFilePaths = useCallback((): string[] => {
    const idPath = buildIdPathMap(fileTree);
    const paths: string[] = [];
    for (const id of Array.from(selectedFileIds)) {
      const p = idPath.get(id);
      if (p) paths.push(p);
    }
    return paths;
  }, [fileTree, selectedFileIds]);

  // Create folder
  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) {
      return;
    }

    setIsCreatingFolder(true);
    try {
      const data = await apiPost<any>(`/api/projects/${projectId}/folders`, {
        name: newFolderName.trim(),
        parentFolderPath: getCurrentFolderId(),
      });

      if (!data || data.code !== 200) {
        return;
      }

      resetProjectFilesCache(projectId);
      await loadProjectFilesFromStore(projectId);
      setIsCreateFolderModalOpen(false);
      setNewFolderName('');
      showToast(
        t('toast.folderCreatedSuccessfully', { name: newFolderName.trim() }),
        'success'
      );
    } catch (error) {
      console.error('Failed to create folder:', error);
      showToast(t('toast.failedToCreateFolder'), 'error');
    } finally {
      setIsCreatingFolder(false);
    }
  }, [
    newFolderName,
    projectId,
    getCurrentFolderId,
    resetProjectFilesCache,
    loadProjectFilesFromStore,
    t,
  ]);

  // Open edit folder dialog
  const handleOpenEditFolder = useCallback(
    (folder: FileNode, event: React.MouseEvent) => {
      event.stopPropagation();
      setEditingFolder(folder);
      setEditFolderName(folder.name);
      setIsEditFolderModalOpen(true);
    },
    []
  );

  // Edit folder
  const handleEditFolder = useCallback(async () => {
    if (!editFolderName.trim() || !editingFolder) {
      return;
    }

    if (editFolderName.trim() === editingFolder.name) {
      showToast('Folder name has not been changed.', 'info');
      return;
    }

    setIsEditingFolder(true);
    try {
      const data = await apiPatch<any>(
        `/api/projects/${projectId}/files/modification`,
        {
          name: editFolderName.trim(),
          filePath: editingFolder.path,
        }
      );

      if (!data || data.code !== 200) {
        return;
      }

      resetProjectFilesCache(projectId);
      await loadProjectFilesFromStore(projectId);

      // 如果重命名的文件/文件夹正在预览，更新预览窗口
      const currentPreviewPath = previewFilePathRef.current;
      if (
        currentPreviewPath &&
        onUpdatePreviewRef.current &&
        editingFolder.path
      ) {
        const oldPath = editingFolder.path;
        const newName = editFolderName.trim();

        if (editingFolder.type === 'file' && currentPreviewPath === oldPath) {
          // 文件重命名：替换路径中的最后一段为新名称
          const lastSlashIndex = oldPath.lastIndexOf('/');
          const newPath =
            lastSlashIndex >= 0
              ? oldPath.substring(0, lastSlashIndex + 1) + newName
              : newName;
          const token = localStorage.getItem('user-token');
          const newFileUrl = `${window.location.origin}/api/projects/${projectId}/files/content?filePath=${encodeURIComponent(newPath)}&Authorization=${token}&asAttachment=false`;
          onUpdatePreviewRef.current(newName, newPath, newFileUrl);
        } else if (editingFolder.type === 'folder') {
          // 文件夹重命名：检查预览文件是否在该文件夹内
          if (currentPreviewPath.startsWith(oldPath + '/')) {
            const lastSlashIndex = oldPath.lastIndexOf('/');
            const newFolderPath =
              lastSlashIndex >= 0
                ? oldPath.substring(0, lastSlashIndex + 1) + newName
                : newName;
            const newPreviewPath =
              newFolderPath + currentPreviewPath.substring(oldPath.length);
            const previewFilename = newPreviewPath.split('/').pop() || '';
            const token = localStorage.getItem('user-token');
            const newFileUrl = `${window.location.origin}/api/projects/${projectId}/files/content?filePath=${encodeURIComponent(newPreviewPath)}&Authorization=${token}&asAttachment=false`;
            onUpdatePreviewRef.current(
              previewFilename,
              newPreviewPath,
              newFileUrl
            );
          }
        }
      }

      setIsEditFolderModalOpen(false);
      setEditingFolder(null);
      setEditFolderName('');
      showToast(
        t('toast.folderRenamedSuccessfully', { name: editFolderName.trim() }),
        'success'
      );
    } catch (error) {
      console.error('Failed to edit folder:', error);
      showToast(t('toast.failedToEditFolder'), 'error');
    } finally {
      setIsEditingFolder(false);
    }
  }, [
    editFolderName,
    editingFolder,
    projectId,
    resetProjectFilesCache,
    loadProjectFilesFromStore,
    t,
  ]);

  // Delete folder with confirmation
  const handleDeleteFolder = useCallback(
    (folder: FileNode, event: React.MouseEvent) => {
      event.stopPropagation();
      confirmWithAction(
        {
          title: t('project.deleteFolder'),
          message: t('project.deleteFolderConfirm', { name: folder.name }),
        },
        async () => {
          const filePath = folder.path || folder.name;
          const data = await apiDelete<any>(
            `/api/projects/${projectId}/files/deletion`,
            { filePaths: [filePath] }
          );

          if (!data || data.code !== 200) {
            throw new Error('Failed to delete folder');
          }

          resetProjectFilesCache(projectId);
          await loadProjectFilesFromStore(projectId);

          // 如果删除的文件夹包含正在预览的文件，关闭预览窗口
          const currentPreviewPath = previewFilePathRef.current;
          if (currentPreviewPath && onClosePreviewRef.current) {
            if (
              currentPreviewPath === filePath ||
              currentPreviewPath.startsWith(filePath + '/')
            ) {
              onClosePreviewRef.current();
            }
          }

          showToast(
            t('toast.folderDeletedSuccessfully', { name: folder.name }),
            'success'
          );
        }
      );
    },
    [
      confirmWithAction,
      projectId,
      resetProjectFilesCache,
      loadProjectFilesFromStore,
      t,
    ]
  );

  // Download file
  const handleDownloadFile = useCallback(
    async (file: FileNode, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation();
      }

      try {
        const token = localStorage.getItem('user-token');
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await fetch(
          `/api/projects/${projectId}/files/download`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Timezone: timezone,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ files: [file.path] }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // 从 Content-Disposition 头获取文件名，与 handleBatchDownload 保持一致
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = file.name;
        if (contentDisposition) {
          // 优先匹配 filename*（RFC 5987 编码格式：charset'language'encoded_value）
          const extMatch = contentDisposition.match(
            /filename\*\s*=\s*([^']*)'[^']*'([^;\n]*)/
          );
          if (extMatch && extMatch[2]) {
            filename = decodeURIComponent(extMatch[2]);
          } else {
            const match = contentDisposition.match(
              /filename\s*=\s*(".*?"|[^;\n]*)/
            );
            if (match && match[1]) {
              filename = match[1].replace(/^"|"$/g, '');
            }
          }
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        showToast(
          t('toast.fileDownloadedSuccessfully', { name: file.name }),
          'success'
        );
      } catch (error) {
        console.error('Failed to download file:', error);
        showToast(t('toast.failedToDownloadFile'), 'error');
      }
    },
    [projectId, t]
  );

  // Delete file/folder with confirmation (根据类型动态显示标题)
  const handleDeleteFile = useCallback(
    (file: FileNode, event: React.MouseEvent) => {
      event.stopPropagation();
      const isFolder = file.type === 'folder';
      confirmWithAction(
        {
          title: isFolder ? t('project.deleteFolder') : t('project.deleteFile'),
          message: isFolder
            ? t('project.deleteFolderConfirm', { name: file.name })
            : t('project.deleteFileConfirm', { name: file.name }),
        },
        async () => {
          const filePath = file.path || file.name;
          const data = await apiDelete<any>(
            `/api/projects/${projectId}/files/deletion`,
            { filePaths: [filePath] }
          );

          if (!data || data.code !== 200) {
            throw new Error('Failed to delete file');
          }

          resetProjectFilesCache(projectId);
          await loadProjectFilesFromStore(projectId);

          // 如果删除的文件/文件夹正在预览，关闭预览窗口
          const currentPreviewPath = previewFilePathRef.current;
          if (currentPreviewPath && onClosePreviewRef.current) {
            const isFolder = file.type === 'folder';
            if (isFolder) {
              // 文件夹被删除：检查预览文件是否在该文件夹内
              if (
                currentPreviewPath === filePath ||
                currentPreviewPath.startsWith(filePath + '/')
              ) {
                onClosePreviewRef.current();
              }
            } else {
              // 文件被删除：直接匹配路径
              if (currentPreviewPath === filePath) {
                onClosePreviewRef.current();
              }
            }
          }

          showToast(
            t('toast.fileDeletedSuccessfully', { name: file.name }),
            'success'
          );
        }
      );
    },
    [
      confirmWithAction,
      projectId,
      resetProjectFilesCache,
      loadProjectFilesFromStore,
      t,
    ]
  );

  // Batch download - 一次性下载所有选中的文件和文件夹
  const handleBatchDownload = useCallback(async () => {
    const idPath = buildIdPathMap(fileTree);
    const filePaths: string[] = [];

    for (const id of Array.from(selectedFileIds)) {
      const p = idPath.get(id);
      if (p) {
        filePaths.push(p);
      }
    }

    if (filePaths.length === 0) return;

    try {
      const token = localStorage.getItem('user-token');
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(
        `/api/projects/${projectId}/files/download`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Timezone: timezone,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ files: filePaths }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download files');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // 从 Content-Disposition 头获取文件名，默认 download.zip
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'download.zip';
      if (contentDisposition) {
        // 优先匹配 filename*（RFC 5987 编码格式：charset'language'encoded_value）
        const extMatch = contentDisposition.match(
          /filename\*\s*=\s*([^']*)'[^']*'([^;\n]*)/
        );
        if (extMatch && extMatch[2]) {
          filename = decodeURIComponent(extMatch[2]);
        } else {
          const match = contentDisposition.match(
            /filename\s*=\s*(".*?"|[^;\n]*)/
          );
          if (match && match[1]) {
            filename = match[1].replace(/^"|"$/g, '');
          }
        }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast(
        t('toast.fileDownloadedSuccessfully', {
          name: `${filePaths.length} items`,
        }),
        'success'
      );
    } catch (error) {
      console.error('Failed to batch download files:', error);
      showToast(t('toast.failedToDownloadFile'), 'error');
    }
  }, [fileTree, selectedFileIds, projectId, t]);

  // Batch delete with confirmation
  const handleBatchDelete = useCallback(async () => {
    const filePaths = getSelectedFilePaths();
    if (filePaths.length === 0) return;

    await confirmWithAction(
      {
        title: t('project.deleteSelectedItems'),
        message: t('project.deleteSelectedConfirm', {
          count: filePaths.length,
        }),
      },
      async () => {
        const data = await apiDelete<any>(
          `/api/projects/${projectId}/files/deletion`,
          { filePaths }
        );

        if (!data || data.code !== 200) {
          throw new Error('Failed to delete files');
        }

        resetProjectFilesCache(projectId);
        await loadProjectFilesFromStore(projectId);

        // 如果批量删除的文件中包含正在预览的文件，关闭预览窗口
        const currentPreviewPath = previewFilePathRef.current;
        if (currentPreviewPath && onClosePreviewRef.current) {
          const shouldClose = filePaths.some(
            fp =>
              currentPreviewPath === fp ||
              currentPreviewPath.startsWith(fp + '/')
          );
          if (shouldClose) {
            onClosePreviewRef.current();
          }
        }

        showToast(
          t('project.itemsDeletedSuccess', { count: filePaths.length }),
          'success'
        );
        clearFileSelection();
      }
    );
  }, [
    getSelectedFilePaths,
    confirmWithAction,
    projectId,
    resetProjectFilesCache,
    loadProjectFilesFromStore,
    clearFileSelection,
    t,
  ]);

  // Open move modal
  const handleOpenMoveModal = useCallback(() => {
    setMoveModalPath([]);
    setMoveTargetPath(null);
    setIsMoveModalOpen(true);
  }, []);

  // Move modal current nodes
  const moveModalCurrentNodes = useMemo(() => {
    if (moveModalPath.length === 0)
      return fileTree.filter(n => n.type === 'folder');
    let current = fileTree;
    for (const part of moveModalPath) {
      const folder = current.find(n => n.type === 'folder' && n.id === part.id);
      if (!folder || !folder.children) return [];
      current = folder.children;
    }
    return current.filter(n => n.type === 'folder');
  }, [fileTree, moveModalPath]);

  // Select folder for move
  const handleMoveSelectFolder = useCallback((folder: FileNode) => {
    setMoveTargetPath(folder.path || folder.name);
  }, []);

  // Confirm move
  const handleMoveConfirm = useCallback(async () => {
    const filePaths = getSelectedFilePaths();
    if (filePaths.length === 0) {
      setIsMoveModalOpen(false);
      return;
    }
    setIsMoving(true);
    try {
      const data = await apiPost<any>(`/api/projects/${projectId}/files/move`, {
        filePaths,
        parentFolderPath: moveTargetPath === '/' ? null : moveTargetPath,
      });

      if (!data || data.code !== 200) return;

      resetProjectFilesCache(projectId);
      await loadProjectFilesFromStore(projectId);

      // 检查 results 数组中每一项的状态
      const results: Array<{
        code: number;
        message: string;
        detail?: { resource_type?: string; resource_id?: string };
      }> = data.results || [];
      if (results.length > 0) {
        const successItems = results.filter(r => r.code === 200);
        const failedItems = results.filter(r => r.code !== 200);

        if (failedItems.length === 0) {
          // 全部成功
          showToast(
            t('toast.itemsMovedSuccessfully', { count: successItems.length }),
            'success'
          );
        } else if (successItems.length === 0) {
          // 全部失败
          const failReasons = failedItems.map(r => r.message).join('; ');
          showToast(failReasons, 'error');
        } else {
          // 部分成功
          const failReasons = failedItems.map(r => r.message).join('; ');
          showToast(
            t('toast.itemsMovedPartially', {
              successCount: successItems.length,
              failCount: failedItems.length,
              reasons: failReasons,
            }),
            'warning'
          );
        }
      } else {
        // 没有 results 字段，使用原有逻辑
        showToast(
          t('toast.itemsMovedSuccessfully', { count: filePaths.length }),
          'success'
        );
      }

      clearFileSelection();
      setIsMoveModalOpen(false);
    } catch (error) {
      console.error('Move error:', error);
      showToast(t('toast.failedToMoveFiles'), 'error');
    } finally {
      setIsMoving(false);
    }
  }, [
    getSelectedFilePaths,
    projectId,
    moveTargetPath,
    resetProjectFilesCache,
    loadProjectFilesFromStore,
    clearFileSelection,
    t,
  ]);

  return {
    // Create folder
    isCreateFolderModalOpen,
    newFolderName,
    isCreatingFolder,
    newFolderNameValidation,
    setIsCreateFolderModalOpen,
    setNewFolderName,
    handleCreateFolder,

    // Edit folder
    isEditFolderModalOpen,
    editingFolder,
    editFolderName,
    isEditingFolder,
    editFolderNameValidation,
    setIsEditFolderModalOpen,
    setEditFolderName,
    setEditingFolder,
    handleOpenEditFolder,
    handleEditFolder,

    // Delete folder
    handleDeleteFolder,

    // Delete file
    handleDeleteFile,

    // Download file
    handleDownloadFile,

    // Batch operations
    handleBatchDelete,
    handleBatchDownload,

    // Confirm dialog props
    fileConfirmDialogProps,

    // Move operations
    isMoveModalOpen,
    moveModalPath,
    moveTargetPath,
    isMoving,
    moveModalCurrentNodes,
    setIsMoveModalOpen,
    setMoveModalPath,
    setMoveTargetPath,
    handleOpenMoveModal,
    handleMoveSelectFolder,
    handleMoveConfirm,
  };
}
